import classnames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import Button from '@/components/button/Button';
import { ButtonStyle } from '@/components/button/ButtonStyle.enum';
import TwoButtonComponent from '@/components/button/TwoButtonComponent';
import { TabSwitch } from '@/components/TabSwitch';

import { WEBAPP_URL } from '@/constants/http-constants';

import { addUtmParams } from '@/helpers/utm-helper';
import { stringFormatJsx } from '@cineamo/ui/src/helpers/string-helper';

import useCookies from '@/hooks/useCookies';

import { IconLock16 } from '@cineamo/ui/src/icons/ic-lock';

import Modal, { ModalProps } from './Modal';

type CookiePreferencesModalProps = ModalProps & {
    onBackClick?: () => void;
};

type CookiePreferencesModalSwitchAndTextContent = {
    title: string;
    text: string;
    isCookieUserEditable?: boolean;
    onSwitchChange?: (state: boolean) => void;
    switchState?: boolean;
};

export function CookiePreferencesModal(props: CookiePreferencesModalProps) {
    const t = useTranslations();
    const locale = useLocale();

    const domainConfiguration = props.domainConfiguration;

    const {
        essentialCookiesPermissionState,
        functionalCookiesPermissionState,
        analysisCookiesPermissionState,
        setFunctionalCookiesPermissionState,
        setAnalysisCookiesPermissionState,
        save,
        rejectAllOptionalCookies
    } = useCookies();

    const switchAndTextContents: CookiePreferencesModalSwitchAndTextContent[] = [
        {
            title: t('data-protection-settings.essential.title'),
            text: t('data-protection-settings.essential.text'),
            isCookieUserEditable: false
        },
        {
            title: t('data-protection-settings.functional.title'),
            text: t('data-protection-settings.functional.text'),
            onSwitchChange: (state) => setFunctionalCookiesPermissionState(state),
            switchState: functionalCookiesPermissionState
        },
        {
            title: t('data-protection-settings.analysis.title'),
            text: t('data-protection-settings.analysis.text'),
            onSwitchChange: (state) => setAnalysisCookiesPermissionState(state),
            switchState: analysisCookiesPermissionState
        }
    ];

    function saveCookiesPreferenceAndClose() {
        save();
        props.setModalIsOpen?.(false);
    }

    function rejectAllOptionalCookiesAndClose() {
        rejectAllOptionalCookies();
        props.setModalIsOpen?.(false);
    }

    return (
        <Modal
            {...props}
            onClose={props.onBackClick || null}
            className="h-[100dvh] lg:!h-[70dvh] !my-24">
            <div className="flex flex-col h-full space-y-6">
                <div className="flex flex-row space-x-12 items-center">
                    <span className="text-body-bold-16">{t('data-protection-settings.your-privacy')}</span>
                    <IconLock16
                        className="flex-none"
                        style={
                            domainConfiguration
                                ? {
                                      color: domainConfiguration.style.primaryColor,
                                      filter: 'brightness(80%)'
                                  }
                                : undefined
                        }
                    />
                </div>
                <div className="flex flex-col flex-auto space-y-16 overflow-auto">
                    <div className="flex flex-col sm:h-auto flex-auto space-y-24">
                        <span className="text-body-12">
                            {stringFormatJsx(
                                t('data-protection-settings.description', { 0: '{0}' }),
                                <a
                                    className="underline"
                                    href={addUtmParams(`${WEBAPP_URL}/data-protection?locale=${locale}`, {
                                        utmSource: 'cinema_website',
                                        utmContent: 'cookie_preferences_modal-data_protection',
                                        utmCampaign: `cinema_${domainConfiguration.cinema.id}-${domainConfiguration.cinema.name}`,
                                        utmMedium: 'website'
                                    })}
                                    target="_blank">
                                    {t('data-protection-settings.privacy-policy')}
                                </a>
                            )}
                        </span>
                        <div className="flex flex-col w-full space-y-16">
                            {switchAndTextContents.map((item, index) => (
                                <div
                                    key={index}
                                    className="w-full flex flex-col md:flex-row md:space-x-24">
                                    <div className="flex flex-col w-full md:w-1/4 order-2 md:order-1">
                                        {item.isCookieUserEditable !== false ? (
                                            <TabSwitch
                                                className="w-full rounded-4 bg-gray"
                                                textClassName="uppercase"
                                                tabClassName="max-h-32"
                                                activeTabClassName="opacity-100 max-h-32 rounded-4"
                                                activeTabCss={
                                                    domainConfiguration
                                                        ? {
                                                              backgroundColor: domainConfiguration.style.primaryColor,
                                                              color: domainConfiguration.style.onPrimaryColor
                                                          }
                                                        : undefined
                                                }
                                                state={item.switchState}
                                                onChange={item.onSwitchChange}
                                                onText={t('common.yes')}
                                                offText={t('common.no')}
                                            />
                                        ) : (
                                            <Button
                                                className="w-full"
                                                style={ButtonStyle.default_on_white_bg}
                                                css={
                                                    domainConfiguration
                                                        ? {
                                                              backgroundColor: domainConfiguration.style.primaryColor,
                                                              color: domainConfiguration.style.onPrimaryColor
                                                          }
                                                        : undefined
                                                }
                                                disabled>
                                                {essentialCookiesPermissionState ? t('common.yes') : t('common.no')}
                                            </Button>
                                        )}
                                    </div>
                                    <div className="flex flex-col w-full md:w-3/4 order-1 md:order-2 mb-8 md:mb-0">
                                        <span className="text-body-bold-16">{item.title}</span>
                                        <span className="text-body-12">{item.text}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="text-body-12">{t('data-protection-settings.footer-text')}</div>
                    </div>

                    <TwoButtonComponent
                        primaryLabel={t('common.save')}
                        primaryAction={saveCookiesPreferenceAndClose}
                        primaryButtonProps={{
                            className: classnames('h-48'),
                            css: domainConfiguration
                                ? {
                                      backgroundColor: domainConfiguration.style.primaryColor,
                                      color: domainConfiguration.style.onPrimaryColor
                                  }
                                : undefined
                        }}
                        secondaryLabel={t('data-protection-settings.reject')}
                        secondaryAction={rejectAllOptionalCookiesAndClose}
                        secondaryButtonProps={{
                            className: classnames('h-48'),
                            css: domainConfiguration
                                ? {
                                      borderColor: domainConfiguration.style.primaryColor,
                                      color: domainConfiguration.style.primaryColor
                                  }
                                : undefined
                        }}
                        stretched
                    />
                </div>
            </div>
        </Modal>
    );
}
